function any(func) {
    return func ? this.some((_, i) => func(_, i)) : this.length > 0;
}

function first(func) {
    return this.find((_, i) => (func ? func(_, i) : true));
}

function select(func) {
    return this.map((t, i) => func(t, i));
}

function groupBy(func) {
    const map = new Map();
    this.forEach((value, i) => {
        const obj = func(value, i);
        if (map.get(obj)) {
            map.get(obj).push(value);
        }
        else {
            map.set(obj, [value]);
        }
    });
    return map;
}

function selectMany(func) {
    return this.reduce((arr, t) => {
        return arr.concat(t);
    }, []).map((t, i) => func(t, i));
}

function intersperse(value) {
    return this.reduce((arr, t, i) => {
        arr.push(t);
        if (i !== this.length - 1) {
            arr.push(value);
        }
        return arr;
    }, []);
}

function all(func) {
    return func ? this.every((_, i) => func(_, i)) : this.length > 0;
}

function sum(func) {
    return this.map(_ => func(_)).reduce((a, b) => (a += b), 0);
}

function min(func) {
    return this.any() ? Math.min(...this.map(_ => func(_))) : NaN;
}

function max(func) {
    return this.any() ? Math.max(...this.map(_ => func(_))) : NaN;
}

function average(func) {
    return this.map(_ => func(_)).sum(_ => _) / this.length;
}

function sortAlphabetical(a, b) {
    return new Intl.Collator().compare(a, b);
}
function sortNumerical(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
function sort(a, b) {
    if (typeof a === 'string' && typeof b === 'string') {
        return sortAlphabetical(a, b);
    }
    if (typeof a === 'number' && typeof b === 'number') {
        return sortNumerical(a, b);
    }
    return 0;
}

function median(func) {
    if (!this.any()) {
        return NaN;
    }
    if (this.length === 1) {
        return func(this[0]);
    }
    const sorted = this.sort((a, b) => sort(func(a), func(b)));
    const x = [sorted[Math.floor((sorted.length - 1) / 2)], sorted[Math.ceil((sorted.length - 1) / 2)]];
    return x.map(_ => func(_)).average(_ => _);
}

function mode(func) {
    if (!this.any()) {
        return [];
    }
    const groups = this.groupBy(_ => func(_));
    const max = Array.from(groups.keys())
        .map(key => groups.get(key).length)
        .max(_ => _);
    return Array.from(groups.keys()).filter(key => groups.get(key).length === max);
}

function intersect(arr, func) {
    return this.filter(x => arr.any(y => (func ? func(x, y) : x === y))).distinct(func);
}

function except(arr, func) {
    const intersect = this.intersect(arr, func);
    return this.concat(arr)
        .filter(x => !intersect.any(y => (func ? func(x, y) : x === y)))
        .distinct(func);
}

function distinct(func) {
    return this.filter((value, i, arr) => arr.indexOf(arr.first(x => (func ? func(value, x) : x === value))) === i);
}

function last(func) {
    return [...this].reverse().find((_, i) => (func ? func(_, i) : true));
}

function take(count) {
    return this.slice(0, count);
}

function skip(count) {
    return this.slice(count, this.length);
}

function skipWhile(func) {
    return this.skip(this.indexOf(this.first(_ => !func(_))));
}

function takeWhile(func) {
    return this.take(this.indexOf(this.first(_ => !func(_))));
}

function toDictionary(func) {
    return this.reduce((obj, value, i) => {
        obj[func(value, i)] = value;
        return obj;
    }, {});
}

function toMap(func) {
    return this.reduce((obj, value, i) => {
        obj.set(func(value, i), value);
        return obj;
    }, new Map());
}

function orderBy(func) {
    const arr = this.sort((a, b) => sort(func(a), func(b)));
    arr.orders = [func];
    return arr;
}

function sortThenBy(arr, orders) {
    orders = Object.assign([], orders);
    const order = orders.shift();
    if (orders.length === 0) {
        return arr.orderBy(order);
    }
    else {
        return Array.from(arr.groupBy(order).values()).reduce((a, value) => a.concat(sortThenBy(value, Object.assign([], orders))), []);
    }
}
function thenBy(func) {
    this.orders.push(func);
    const sorted = sortThenBy(this, this.orders);
    sorted.orders = this.orders;
    return sorted;
}

function insertAt(index, ...values) {
    for (let i = values.length - 1; i >= 0; i--) {
        this.splice(index, 0, values[i]);
    }
}

function remove(value) {
    const index = this.indexOf(value);
    if (index > -1) {
        this.splice(index, 1);
    }
    return this;
}

function removeWhere(func) {
    this.filter(func).forEach(value => this.remove(value));
}

function removeAt(index) {
    this.remove(this[index]);
}

function innerJoin(inner, outerKey, innerKey, selector, func) {
    return this.map((t) => {
        return { t, u: inner.first(u => (func ? func(outerKey(t), innerKey(u)) : outerKey(t) === innerKey(u))) };
    })
        .filter(value => value.u != null)
        .map(value => selector(value.t, value.u));
}

function leftJoin(inner, outerKey, innerKey, selector, func) {
    return this.map((t) => {
        return { t, u: inner.first(u => (func ? func(outerKey(t), innerKey(u)) : outerKey(t) === innerKey(u))) };
    }).map(value => selector(value.t, value.u));
}

function location(func) {
    for (let i = 0; i < this.length; i++) {
        if (func(this[i])) {
            return i;
        }
    }
    return -1;
}

Array.prototype.any = any;
Array.prototype.all = all;
Array.prototype.first = first;
Array.prototype.select = select;
Array.prototype.groupBy = groupBy;
Array.prototype.selectMany = selectMany;
Array.prototype.intersperse = intersperse;
Array.prototype.sum = sum;
Array.prototype.min = min;
Array.prototype.max = max;
Array.prototype.average = average;
Array.prototype.median = median;
Array.prototype.mode = mode;
Array.prototype.intersect = intersect;
Array.prototype.except = except;
Array.prototype.distinct = distinct;
Array.prototype.last = last;
Array.prototype.take = take;
Array.prototype.skip = skip;
Array.prototype.skipWhile = skipWhile;
Array.prototype.takeWhile = takeWhile;
Array.prototype.toDictionary = toDictionary;
Array.prototype.toMap = toMap;
Array.prototype.orderBy = orderBy;
Array.prototype.thenBy = thenBy;
Array.prototype.insertAt = insertAt;
Array.prototype.remove = remove;
Array.prototype.removeWhere = removeWhere;
Array.prototype.removeAt = removeAt;
Array.prototype.innerJoin = innerJoin;
Array.prototype.leftJoin = leftJoin;
Array.prototype.location = location;

/* istanbul ignore next*/
function tsLinq() {
    return ![].any();
}

/**
 * Generated bundle index. Do not edit.
 */

export { tsLinq };
